import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowLeft, 
  Shield,
  Users,
  UserCheck,
  UserX,
  Trash2,
  Search,
  MoreVertical,
  BarChart3,
  Building2,
  FolderKanban,
  Palette,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('user');
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if user is admin (is_admin flag or role case-insensitive)
  const isAdmin = Boolean(
    user?.is_admin === true ||
    (user?.role != null && String(user.role).trim().toLowerCase() === 'admin')
  );

  useEffect(() => {
    // Check admin status immediately
    if (!user) {
      // Still loading user data
      return;
    }
    
    if (!isAdmin) {
      // Show error toast and redirect
      toast.error('Admin access required', {
        description: 'You do not have permission to access this page.',
        duration: 3000,
      });
      // Redirect after a short delay to ensure toast is visible
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
      return;
    }
    
    // Only fetch data if user is admin
    if (isAdmin) {
      fetchUsers();
      fetchStats();
    }
  }, [user, token, isAdmin, navigate]);

  const fetchUsers = async () => {
    if (!isAdmin) return;
    
    try {
      const response = await fetch(`${API_URL}/admin/users?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else if (response.status === 403) {
        toast.error('Admin access required', {
          description: 'Your session may have expired or you lost admin privileges.',
          duration: 3000,
        });
        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/admin/users/${selectedUser.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role: newRole,
          is_admin: newRole === 'admin'
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
        toast.success(`User role updated to ${newRole}`);
        setIsRoleDialogOpen(false);
        setSelectedUser(null);
        fetchStats(); // Refresh stats
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update user role');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
        toast.success('User deleted successfully');
        setIsDeleteDialogOpen(false);
        setSelectedUser(null);
        fetchStats(); // Refresh stats
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setIsUpdating(false);
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role || 'user');
    setIsRoleDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const filteredUsers = users.filter(u => {
    const query = searchQuery.toLowerCase();
    return (
      u.name?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query) ||
      u.role?.toLowerCase().includes(query)
    );
  });

  // Show loading or redirect if not admin
  if (!user) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#1a1d21]' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a1d21]' : 'bg-gray-50'} pb-20 overflow-x-hidden`}>
      <div className="max-w-7xl mx-auto pt-4 sm:pt-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4 touch-manipulation`}
          >
            <ArrowLeft size={20} />
            <span className="text-sm sm:text-base">Back to Dashboard</span>
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <Shield size={24} className={isDark ? 'text-white' : 'text-gray-900'} />
                <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Admin Panel
                </h1>
              </div>
              <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Manage users and view system statistics
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className={`${isDark ? 'bg-[#0F2942] border-white/20' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Users</p>
                  <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {stats.total_users || 0}
                  </p>
                </div>
                <Users className={isDark ? 'text-blue-400' : 'text-blue-600'} size={24} />
              </div>
            </div>

            <div className={`${isDark ? 'bg-[#0F2942] border-white/20' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Admin Users</p>
                  <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {stats.admin_users || 0}
                  </p>
                </div>
                <Shield className={isDark ? 'text-purple-400' : 'text-purple-600'} size={24} />
              </div>
            </div>

            <div className={`${isDark ? 'bg-[#0F2942] border-white/20' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Workspaces</p>
                  <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {stats.total_workspaces || 0}
                  </p>
                </div>
                <Building2 className={isDark ? 'text-green-400' : 'text-green-600'} size={24} />
              </div>
            </div>

            <div className={`${isDark ? 'bg-[#0F2942] border-white/20' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Projects</p>
                  <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {stats.total_projects || 0}
                  </p>
                </div>
                <Briefcase className={isDark ? 'text-orange-400' : 'text-orange-600'} size={24} />
              </div>
            </div>
          </div>
        )}

        {/* Users Management Section */}
        <div className={`${isDark ? 'bg-[#0F2942] border-white/20' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                User Management
              </h2>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                View and manage all users in the system
              </p>
            </div>
            <div className="relative w-64">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} size={16} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className={`pl-9 ${isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' : ''}`}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading users...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-white/10' : ''}>
                    <TableHead className={isDark ? 'text-gray-300' : ''}>User</TableHead>
                    <TableHead className={isDark ? 'text-gray-300' : ''}>Email</TableHead>
                    <TableHead className={isDark ? 'text-gray-300' : ''}>Role</TableHead>
                    <TableHead className={isDark ? 'text-gray-300' : ''}>Created</TableHead>
                    <TableHead className={isDark ? 'text-gray-300' : ''}></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {searchQuery ? 'No users found matching your search' : 'No users found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((u) => (
                      <TableRow key={u.id} className={isDark ? 'border-white/10 hover:bg-white/5' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                              style={{ backgroundColor: u.avatar_color || '#6366F1' }}
                            >
                              {u.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {u.name || 'Unknown'}
                                {u.id === user?.id && (
                                  <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    (You)
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                          {u.email}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              u.is_admin === true || (u.role != null && String(u.role).trim().toLowerCase() === 'admin')
                                ? isDark
                                  ? 'bg-purple-500/20 text-purple-300'
                                  : 'bg-purple-100 text-purple-700'
                                : isDark
                                ? 'bg-gray-500/20 text-gray-300'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {u.is_admin === true || (u.role != null && String(u.role).trim().toLowerCase() === 'admin') ? (
                              <>
                                <Shield size={12} />
                                Admin
                              </>
                            ) : (
                              <>
                                <Users size={12} />
                                User
                              </>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                          {u.created_at
                            ? new Date(u.created_at).toLocaleDateString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : ''}
                              >
                                <MoreVertical size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className={isDark ? 'bg-[#0F2942] border-white/20' : ''}>
                              <DropdownMenuItem
                                onClick={() => openRoleDialog(u)}
                                className={isDark ? 'text-gray-300 hover:bg-white/10' : ''}
                              >
                                <UserCheck size={14} className="mr-2" />
                                Change Role
                              </DropdownMenuItem>
                              {u.id !== user?.id && (
                                <DropdownMenuItem
                                  onClick={() => openDeleteDialog(u)}
                                  className={`${isDark ? 'text-red-400 hover:bg-red-500/20' : 'text-red-600'}`}
                                >
                                  <Trash2 size={14} className="mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Update Role Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className={isDark ? 'bg-[#0F2942] border-white/20 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription className={isDark ? 'text-gray-400' : ''}>
              Update the role for {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Role</Label>
              <div className="flex gap-4 mt-2">
                <button
                  onClick={() => setNewRole('user')}
                  className={`flex-1 px-4 py-3 rounded-lg border transition-all ${
                    newRole === 'user'
                      ? isDark
                        ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                        : 'border-blue-500 bg-blue-50 text-blue-600'
                      : isDark
                      ? 'border-white/20 hover:border-white/40 text-gray-300'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 justify-center">
                    <Users size={16} />
                    <span className="font-medium">User</span>
                  </div>
                </button>
                <button
                  onClick={() => setNewRole('admin')}
                  className={`flex-1 px-4 py-3 rounded-lg border transition-all ${
                    newRole === 'admin'
                      ? isDark
                        ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                        : 'border-purple-500 bg-purple-50 text-purple-600'
                      : isDark
                      ? 'border-white/20 hover:border-white/40 text-gray-300'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 justify-center">
                    <Shield size={16} />
                    <span className="font-medium">Admin</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRoleDialogOpen(false);
                setSelectedUser(null);
              }}
              className={isDark ? 'border-white/20 hover:bg-white/10' : ''}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              disabled={isUpdating || !selectedUser}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isUpdating ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className={isDark ? 'bg-[#0F2942] border-white/20 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription className={isDark ? 'text-gray-400' : ''}>
              Are you sure you want to delete {selectedUser?.name || selectedUser?.email}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSelectedUser(null);
              }}
              className={isDark ? 'border-white/20 hover:bg-white/10' : ''}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteUser}
              disabled={isUpdating}
              variant="destructive"
            >
              {isUpdating ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
